/**
 * to enable views to be reused, is needed to update ROUTES of the routing module for the specific view to reuse.
 * this flag is {reuseRoute: true}
 * example route WITHOUT flag:
 *
 * const routes: Routes = [
 *   {
 *    path: '',
 *    component: IndexCandidatesComponent,
 *    canActivate: [AuthGuard],
 *    data: {
 *      title: 'Candidates'
 *    }
 *  }
 * ];
 *
 * example route WITH flag:
 *
 * const routes: Routes = [
 *   {
 *    path: '',
 *    component: IndexCandidatesComponent,
 *    canActivate: [AuthGuard],
 *    data: {
 *      title: 'Candidates',
 *      reuseRoute: true
 *    }
 *  }
 * ];
 */

import { ActivatedRouteSnapshot, RouteReuseStrategy, DetachedRouteHandle, UrlSegment } from '@angular/router';

interface RouteStorageObject {
  snapshot: ActivatedRouteSnapshot; // used to store the current route snapshot
  handle: DetachedRouteHandle; // ??
  y: number; // used to store scrollY position
}

export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  private _handle: DetachedRouteHandle;
  private _snapshot: ActivatedRouteSnapshot;

  /**
   * map where are stored views to be reused
   */
  storedRoutes: { [key: string]: RouteStorageObject } = {};

  /**
   * method for scroll window
   */
  private windowScroll(y: number): void {
    window.scrollTo(0, y);
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    // if reuseRoute is true, we store in storedRoutes all we need to reuse this view with also scrollPosition
    if (route.data.reuseRoute) {
      const id = this.createIdentifier(route);
      const y = window.scrollY;
      const __handle = this._handle;
      const __snapshot = this._snapshot;
      this.storedRoutes[id] = { handle: __handle, snapshot: __snapshot, y };
    }

    return route.data.reuseRoute || false;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const id = this.createIdentifier(route);
    const y = window.scrollY;
    if (route.data.reuseRoute && id.length > 0) {
      this._handle = handle;
      this._snapshot = route;
      if (this.storedRoutes[id]) {
        this.storedRoutes[id].handle = handle;
        this.storedRoutes[id].snapshot = route;
      } else {
        this.storedRoutes[id] = { handle, snapshot: route, y };
      }
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const id = this.createIdentifier(route);
    const storedObject = this.storedRoutes[id];
    const canAttach = !!route.routeConfig && !!storedObject;
    if (!canAttach) {
      return false;
    }

    const paramsMatch = this.compareObjects(route.params, storedObject.snapshot.params);
    const queryParamsMatch = this.compareObjects(route.queryParams, storedObject.snapshot.queryParams);

    /*
      uncomment below code for local debuging
    */
    // console.log('deciding to attach...', route, 'does it match?');
    // console.log('param comparison:', paramsMatch);
    // console.log('query param comparison', queryParamsMatch);
    // console.log(storedObject.snapshot, 'return: ', paramsMatch && queryParamsMatch);

    setTimeout(() => {
      this.windowScroll(this.storedRoutes[id].y || 0);
    }, 250);

    return paramsMatch && queryParamsMatch;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    const id = this.createIdentifier(route);
    if (!route.routeConfig || !this.storedRoutes[id]) {
      return null;
    }
    return this.storedRoutes[id].handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  /*
   * method used to create a unique identifier
   */
  private createIdentifier(route: ActivatedRouteSnapshot) {
    // Build the complete path from the root to the input route
    const segments: UrlSegment[][] = route.pathFromRoot.map(r => r.url);
    const subpaths = ([] as UrlSegment[]).concat(...segments).map(segment => segment.path);
    // Result: ${route_depth}-${path}
    return segments.length + '-' + subpaths.join('/');
  }

  private compareObjects(base: any, compare: any): boolean {
    // loop through all properties
    for (const baseProperty in { ...base, ...compare }) {

      // determine if comparrison object has that property, if not: return false
      if (compare.hasOwnProperty(baseProperty)) {
        switch (typeof base[baseProperty]) {
          // if one is object and other is not: return false
          // if they are both objects, recursively call this comparison function
          case 'object':
            if (typeof compare[baseProperty] !== 'object' || !this.compareObjects(base[baseProperty], compare[baseProperty])) {
              return false;
            }
            break;
          // if one is function and other is not: return false
          // if both are functions, compare function.toString() results
          case 'function':
            if (typeof compare[baseProperty] !== 'function' || base[baseProperty].toString() !== compare[baseProperty].toString()) {
              return false;
            }
            break;
          // otherwise, see if they are equal using coercive comparison
          default:
            // tslint:disable-next-line triple-equals
            if (base[baseProperty] != compare[baseProperty]) {
              return false;
            }
        }
      } else {
        return false;
      }
    }

    // returns true only after false HAS NOT BEEN returned through all loops
    return true;
  }
}
